import { jotaiStore } from '@onramp/state/recoil/utils';
import { coerceError, HandledError, NetworkError, reportError } from '@onramp/utils/errors';
// eslint-disable-next-line import/no-cycle
import { fetchWithCommonHeaders } from '@onramp/utils/requests';

import { getCookie, setCookie } from '../cookies/clientUtils';

import type { KillSwitchesConfigType, KillSwitchesResponse } from './killSwitchConfiguration';
import {
  KILL_SWITCH_PARAMS,
  KILL_SWITCH_PATH_CLIENT,
  KillSwitchesConfigSchema,
  KillSwitchesCookieSchema,
  resolveKillSwitches,
} from './killSwitchConfiguration';
import { killSwitchesAtom } from './store';

export const getOverriddenKillswitchesFromCookie = () => {
  const parsed = KillSwitchesCookieSchema.safeParse(
    getCookie({ cookieName: 'overridenKillSwitches' }),
  );
  return parsed.success ? parsed.data?.killswitches : undefined;
};

export const fetchKillSwitches = async (): Promise<KillSwitchesConfigType> => {
  const killSwitchesOverride = getOverriddenKillswitchesFromCookie() ?? {};

  try {
    const killSwitchesCache = jotaiStore.get(killSwitchesAtom);
    if (killSwitchesCache) {
      return resolveKillSwitches(killSwitchesCache, killSwitchesOverride);
    }
    // eslint-disable-next-line no-empty
  } catch {}

  const paramsJson = JSON.stringify(KILL_SWITCH_PARAMS);

  // The API expects a base64 encoded query param.
  const url = `${KILL_SWITCH_PATH_CLIENT}?q=${window.btoa(paramsJson)}`;

  try {
    const res = await fetchWithCommonHeaders(url);
    if (!res.ok) {
      throw new Error('Failed to fetch kill switches');
    }

    try {
      const killSwitches = KillSwitchesConfigSchema.parse(await res.json()).killswitches;
      jotaiStore.set(killSwitchesAtom, killSwitches);

      return resolveKillSwitches(killSwitches, killSwitchesOverride);
    } catch (e) {
      throw new HandledError({
        handledError: e instanceof Error ? e : undefined,
        message: "Zod didn't parse the killswitches successfully",
      });
    }
  } catch (e) {
    reportError(coerceError(e, { fallback: NetworkError }));
    return resolveKillSwitches(undefined);
  }
};

export const setKillSwitchOverrideCookie = (killSwitches: KillSwitchesResponse) => {
  setCookie({ cookieName: 'overridenKillSwitches', value: JSON.stringify(killSwitches) });
};
